/* eslint @typescript-eslint/strict-boolean-expressions: 0 */
import {
  find,
  isEmpty,
  isNil,
  isNumber,
  join,
  keys,
  map,
  reduce,
  size
} from 'lodash/fp'
import { Chart } from 'react-chartjs-2'
import {
  BarController,
  BarElement,
  Chart as ChartJS,
  CategoryScale,
  LineController,
  Filler,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  type ChartConfiguration,
  Legend
} from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import {
  ElectricityChartDailyKind,
  type ElectricityChartMonthlyKind,
  type Frame,
  type ElectricityDaily,
  type ElectricityWeekly,
  type ElectricityMonthly,
  type ElectricityYearly,
  type ElectricityChartYearlyKind
} from '../../../../cdk/bapp/lib/lambda-api/src/types'
import React, { type ReactElement } from 'react'
import log from 'loglevel'
import {
  Loading,
  NotFound,
  type RaRecord,
  useNotify,
  useTranslate
} from 'react-admin'
import {
  useGetElectricityDaily,
  useGetElectricityDailyBySpin,
  useGetElectricityDailyTargets,
  useGetElectricityDemand,
  useGetElectricityMonthly,
  useGetElectricityMonthlyBySpin,
  useGetElectricityMonthlyTargets,
  useGetElectricityWeekly,
  useGetElectricityWeeklyBySpin,
  useGetElectricityYearly,
  useGetElectricityYearlyBySpin,
  useGetElectricityYearlyTargets
} from 'components/DataProvider'
import {
  createDailyChart,
  createMonthlyChart,
  createMonthlyHorizontalChart,
  createWeeklyChart,
  createYearlyChart
} from './chartjs'
import { type ScrollProps } from 'components/Util'
import { type ConfigProps } from 'components/Dashboard'
import { Box, Typography } from '@mui/material'
import { flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { format, parseISO } from 'date-fns/fp'
import { demandHints } from './demandHints'

ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Filler,
  Title,
  Legend,
  annotationPlugin,
  ChartDataLabels
)

const defaultWidth = '100%'
const defaultHeight = 500

const AmountLabel: React.FC<{
  amountsId?: string
  amountsTotal?: { amounts: number; compareAmounts: number } | null | undefined
}> = ({ amountsId, amountsTotal }) => {
  React.useEffect(() => {
    if (!isNil(amountsId)) {
      const amountsContainer = document.getElementById(amountsId)
      if (!isNil(amountsContainer)) {
        const root = createRoot(amountsContainer)
        if (!isNil(amountsTotal)) {
          flushSync(() => {
            root.render(createAmountsLabel(amountsTotal))
          })
        } else {
          flushSync(() => {
            root.render(<></>)
          })
        }
      }
    }
  }, [amountsId, amountsTotal])
  return <></>
}
const DemandLabel: React.FC<{
  partner: string
  demandId?: string
  demand?:
    | {
        amount: number
        date: string
        latest: { amount: number | undefined; date: string | undefined }
      }
    | null
    | undefined
  latestLabel?: string
  latestFormat?: string
}> = ({ partner, demandId, demand, latestLabel, latestFormat }) => {
  React.useEffect(() => {
    if (!isNil(demandId) && !isNil(latestLabel) && !isNil(latestFormat)) {
      const amountsContainer = document.getElementById(demandId)
      if (!isNil(amountsContainer)) {
        const root = createRoot(amountsContainer)
        flushSync(() => {
          root.render(
            !isNil(demand) ? (
              createDemandLabel(demand, latestLabel, latestFormat)
            ) : (
              <></>
            )
          )
        })
      }
      const hintContainer = document.getElementById(`${demandId}Hint`)
      if (!isNil(hintContainer)) {
        const hint = find(
          (item: { partner: string; element: () => ReactElement }) =>
            item.partner === partner || item.partner === '*'
        )(demandHints)
        if (!isNil(hint)) {
          const { element } = hint
          const root = createRoot(hintContainer)
          flushSync(() => {
            root.render(!isNil(demand) ? element() : <></>)
          })
        }
      }
    }
  }, [demandId, demand])
  return <></>
}
const SolarChart: React.FC<{
  chart: ChartConfiguration
  width?: number
  height?: number
}> = ({ chart, width, height }) => {
  const { type, options, data, plugins } = chart
  if (isNil(options)) return <></>
  return (
    <div>
      <div id="legend-container" />
      <div>
        <Chart
          type={type}
          width={width ?? defaultWidth}
          height={height ?? defaultHeight}
          options={options}
          plugins={!isNil(plugins) ? plugins : []}
          data={data}
          id="solar-chart"
        />
      </div>
    </div>
  )
}
const createAmountsLabel = (amountsTotal: {
  amounts: number
  compareAmounts: number
}): ReactElement => {
  const { amounts, compareAmounts } = amountsTotal
  const diff = amounts - compareAmounts
  const diffRatio = Math.floor((diff / compareAmounts) * 1000) / 10
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        pt: '0.5em',
        pb: '0.5em',
        backgroundColor: '#FADF8E',
        height: '8em'
      }}>
      <Box sx={{ alignSelf: 'center' }}>
        <Typography variant="h6">電気使用量</Typography>
      </Box>
      <Box sx={{ alignSelf: 'center' }}>
        <Typography variant="h6">{amounts.toLocaleString()}kWh</Typography>
      </Box>
      <Box sx={{ alignSelf: 'center' }}>
        <Typography sx={{ color: 'blue' }}>
          {diff > 0 ? '+' : ''}
          {Math.floor(diff).toLocaleString()}kWh ({diffRatio}%{' '}
          {diff > 0 ? '↑' : diff < 0 ? '↓' : ''})
        </Typography>
      </Box>
    </Box>
  )
}
const createDemandLabel = (
  demand: {
    amount: number
    date: string
    latest: {
      amount: number | undefined
      date: string | undefined
    }
  },
  latestLabel: string,
  latestFormat: string
): ReactElement => {
  const {
    amount,
    date,
    latest: { amount: latestAmount, date: latestDate }
  } = demand
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        pt: '0.5em',
        pb: '0.5em',
        backgroundColor: '#FADF8E',
        height: '8em'
      }}>
      <Box sx={{ alignSelf: 'center' }}>
        <Typography variant="h6">デマンド（DM）</Typography>
      </Box>
      <Box sx={{ alignSelf: 'center' }}>
        <Typography variant="h6">
          最大DM：{amount.toLocaleString()}kW
        </Typography>
      </Box>
      <Box sx={{ alignSelf: 'center' }}>
        <Typography sx={{ textDecoration: 'underline' }}>
          {format('yyyy年M月d日 H:mm')(parseISO(date))}
        </Typography>
      </Box>
      {!isNil(latestAmount) && !isNil(latestDate) && (
        <Box sx={{ alignSelf: 'center' }}>
          {size(latestFormat) < 6 ? (
            <Typography variant="subtitle2" sx={{ color: 'red' }}>
              {latestLabel}DM：{latestAmount.toLocaleString()}kW（
              {format(latestFormat)(parseISO(latestDate))}）
            </Typography>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ color: 'red' }}>
                {latestLabel}DM：{latestAmount.toLocaleString()}kW
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'red' }}>
                （{format(latestFormat)(parseISO(latestDate))}）
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  )
}
export const ElectricityChartDaily: React.FC<
  {
    spin: string
    partner: string
    compareTo: ElectricityChartDailyKind
    targets: Record<string, boolean> | undefined
    date: string
    classsification: string | null | undefined
    record: RaRecord
    showKinds: string[] | undefined
    amountsId?: string | undefined
    demandId?: string | undefined
  } & ScrollProps &
    ConfigProps
> = ({
  spin,
  partner,
  scrollRef,
  compareTo,
  targets: targetsArg,
  date,
  configChart,
  record,
  classsification,
  showKinds,
  amountsId,
  demandId
}) => {
  const translate = useTranslate()
  // const { permissions, isLoading: isLoadingPermissions } = usePermissions()
  const notify = useNotify()
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const targets = !isNil(targetsArg) ? keys(targetsArg) : undefined
  const [amountsTotal, setAmountsTotal] = React.useState<
    | {
        amounts: number
        compareAmounts: number
      }
    | undefined
  >(undefined)
  const [demand, setDemand] = React.useState<
    | {
        amount: number
        date: string
        latest: { amount: number | undefined; date: string | undefined }
      }
    | undefined
    | null
  >(undefined)
  const [targetSpin, setTargetSpin] = React.useState<string | undefined>(
    undefined
  )
  const [targetDate, setTargetDate] = React.useState<string | undefined>(
    undefined
  )
  const { data: electricityDataDaily, isLoading } = isNil(
    process.env['NOT_USE_SPIN']
  )
    ? useGetElectricityDailyBySpin(date, spin, compareTo, !isNil(demandId))
    : useGetElectricityDaily(date, compareTo)
  const { data: demandData, isLoading: isLoadingDemand } =
    useGetElectricityDemand(date, spin)
  const { data: targetsData, isLoading: isLoadingTargets } =
    useGetElectricityDailyTargets(
      date,
      compareTo,
      isNil(targets) ? undefined : targets,
      isNil(targets)
        ? undefined
        : map((target: string) => record[target])(targets)
    )
  React.useEffect(() => {
    if (spin !== targetSpin || date !== targetDate) {
      if (spin !== targetSpin) setTargetSpin(spin)
      if (date !== targetDate) setTargetDate(date)
      setAmountsTotal(undefined)
      setDemand(undefined)
    }
    if (
      !isLoading &&
      !isNil(electricityDataDaily) &&
      !isLoadingDemand &&
      !isNil(demandData) &&
      !isLoadingTargets &&
      !isNil(targetsData)
    ) {
      if (isNil(electricityDataDaily.data)) {
        notify('no data')
        setChart(undefined)
      }
      const rawData = isNil(process.env['NOT_USE_SPIN'])
        ? electricityDataDaily.data
        : find((item: ElectricityDaily) => item.spin === spin)(
            electricityDataDaily.data
          )
      if (!isNil(rawData)) {
        const compareData = isNil(process.env['NOT_USE_SPIN'])
          ? electricityDataDaily.compareData
          : find((item: ElectricityDaily) => item.spin === spin)(
              electricityDataDaily.compareData
            )
        const { additionalData, demandData: latest } = electricityDataDaily
        const { amounts: amountsRaw } = rawData
        // const times = map((frame: Frame) => frame.time)(amountsRaw)
        const amounts = map((frame: Frame) => frame.amount)(amountsRaw)
        const compareAmounts = !isNil(compareData)
          ? map((frame: Frame) => frame.amount)(compareData.amounts)
          : []
        setAmountsTotal({
          amounts: reduce(
            (sum: number, amount: number) => sum + amount,
            0
          )(amounts),
          compareAmounts: reduce(
            (sum: number, amount: number) => sum + amount,
            0
          )(compareAmounts)
        })
        if (!isNil(latest) && !isNil(demandData)) {
          const { amount, date: demandDate } = demandData
          if (!isNil(amount) && !isNil(demandDate)) {
            setDemand({ amount, date: demandDate, latest })
          }
        }
        const additionalAmounts =
          !isNil(targetsData) && !isNil(targetsArg) && !isNil(additionalData)
            ? map((frame: Frame) => frame.amount)(additionalData.amounts)
            : undefined
        const targetsAmounts =
          !isNil(targetsData) && !isNil(targetsArg)
            ? map(
                (item: {
                  targetKey: string
                  targetValue: string
                  data: Record<string, ElectricityDaily>
                }) => {
                  const { data: dataSet, targetKey } = item
                  if (!(targetsArg[targetKey] as boolean)) {
                    return undefined
                  }
                  const data = dataSet[join(',')(showKinds)]
                  if (isNil(data)) return undefined
                  return {
                    key:
                      targetKey === 'addressState'
                        ? '都道府県'
                        : targetKey === 'addressCity'
                        ? '市区町村'
                        : targetKey,
                    data: map((frame: Frame) => frame.amount)(data.amounts)
                  }
                }
              )(targetsData)
            : undefined
        // const maxAmounts = max([
        //   max(amounts),
        //   !isNil(lastWeekAmounts) ? max(lastWeekAmounts) : 0
        // ]) as number
        const { date, maxTemp, minTemp } = rawData
        const {
          date: compareDate,
          maxTemp: compareMaxTemp,
          minTemp: compareMinTemp
        } = compareData ?? {
          date: undefined,
          maxTemp: undefined,
          minTemp: undefined
        }
        const chart = createDailyChart(
          amounts,
          compareAmounts,
          additionalAmounts,
          targetsAmounts,
          { date, maxTemp, minTemp },
          compareTo === ElectricityChartDailyKind.lastWeek
            ? {
                date: compareDate,
                maxTemp: compareMaxTemp,
                minTemp: compareMinTemp,
                type:
                  compareTo === ElectricityChartDailyKind.lastWeek
                    ? 'daily'
                    : compareTo ===
                      ElectricityChartDailyKind.lastYearWithMonthlyAverage
                    ? 'monthly'
                    : 'yearly'
              }
            : {
                date: compareDate,
                maxTemp: compareMaxTemp,
                minTemp: compareMinTemp,
                type: 'daily'
              },
          translate(`resources.spots.daily.compareTo.${compareTo as string}`),
          configChart,
          classsification
        )
        setChart(chart)
      }
    }
  }, [
    spin,
    partner,
    electricityDataDaily,
    isLoading,
    targetsArg,
    showKinds,
    isLoadingDemand,
    demandData,
    isLoadingTargets,
    targetsData,
    targetSpin,
    targetDate
  ])
  scrollRef.current?.scrollIntoView()
  return (
    <>
      {!isNil(amountsId) && (
        <AmountLabel amountsId={amountsId} amountsTotal={amountsTotal} />
      )}
      {!isNil(demandId) && (
        <DemandLabel
          partner={partner}
          demandId={demandId}
          demand={demand}
          latestLabel="対象日"
          latestFormat="H:mm"
        />
      )}
      {isLoading ||
      // isLoadingPermissions ||
      isLoadingDemand ||
      isLoadingTargets ? (
        <Loading />
      ) : !isNil(chart) ? (
        <SolarChart chart={chart} />
      ) : null}
    </>
  )
}
export const ElectricityChartWeekly: React.FC<
  {
    spin: string
    partner: string
    date: string
    classification: string | null | undefined
    record: RaRecord
  } & ConfigProps
> = ({ spin, partner, date, configChart, classification }) => {
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const { data: electricityDataWeekly, isLoading } = isNil(
    process.env['NOT_USE_SPIN']
  )
    ? useGetElectricityWeeklyBySpin(date, spin)
    : useGetElectricityWeekly(date)
  React.useEffect(() => {
    if (!isLoading && !isNil(electricityDataWeekly)) {
      const data = isNil(process.env['NOT_USE_SPIN'])
        ? electricityDataWeekly.data
        : find((item: ElectricityWeekly) => item.spin === spin)(
            electricityDataWeekly.data
          )
      if (!isNil(data)) {
        const compareData = isNil(process.env['NOT_USE_SPIN'])
          ? electricityDataWeekly.compareData
          : find((item: ElectricityWeekly) => item.spin === spin)(
              electricityDataWeekly.compareData
            )
        const amounts = map(
          (item: { date: string; daily: number | null }) => item.daily
        )(data.amounts)
        const lastWeekAmounts = !isNil(compareData)
          ? map((item: { date: string; daily: number | null }) => item.daily)(
              compareData.amounts
            )
          : []
        // const maxAmounts = max([
        //   max(amounts),
        //   !isNil(lastWeekAmounts) ? max(lastWeekAmounts) : 0
        // ]) as number
        createWeeklyChart(
          date,
          amounts,
          lastWeekAmounts,
          '',
          configChart,
          classification
        )
          .then((chart: ChartConfiguration) => {
            setChart(chart)
          })
          .catch(log.error)
      }
    }
  }, [spin, partner, electricityDataWeekly, isLoading, date])
  if (isLoading) return <Loading />
  if (isNil(chart)) return <NotFound />
  return <SolarChart chart={chart} />
}
export const ElectricityChartMonthly: React.FC<
  {
    spin: string
    partner: string
    compareTo: ElectricityChartMonthlyKind
    targets: Record<string, boolean> | undefined
    date: string
    inputDate: string
    classification: string | null | undefined
    showKinds: string[] | undefined
    amountsId?: string | undefined
    demandId?: string | undefined
  } & ConfigProps
> = ({
  spin,
  partner,
  compareTo,
  targets: targetsArg,
  date,
  inputDate,
  configChart,
  record,
  classification,
  showKinds,
  amountsId,
  demandId
}) => {
  const translate = useTranslate()
  // const { permissions, isLoading: isLoadingPermissions } = usePermissions()
  const notify = useNotify()
  const targets = !isNil(targetsArg) ? keys(targetsArg) : undefined
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const [demand, setDemand] = React.useState<
    | {
        amount: number
        date: string
        latest: { amount: number | undefined; date: string | undefined }
      }
    | undefined
    | null
  >(undefined)
  const [amountsTotal, setAmountsTotal] = React.useState<
    | {
        amounts: number
        compareAmounts: number
      }
    | undefined
  >(undefined)
  const [targetSpin, setTargetSpin] = React.useState<string | undefined>(
    undefined
  )
  const [targetDate, setTargetDate] = React.useState<string | undefined>(
    undefined
  )
  const { data: electricityDataMonthly, isLoading } = isNil(
    process.env['NOT_USE_SPIN']
  )
    ? useGetElectricityMonthlyBySpin(date, spin, compareTo, !isNil(demandId))
    : useGetElectricityMonthly(date, compareTo)
  const { data: targetsData, isLoading: isLoadingTargets } =
    useGetElectricityMonthlyTargets(
      date,
      compareTo,
      isNil(targets) ? undefined : targets,
      isNil(targets)
        ? undefined
        : map((target: string) => record[target])(targets)
    )
  const { data: demandData, isLoading: isLoadingDemand } =
    useGetElectricityDemand(inputDate, spin)
  React.useEffect(() => {
    if (spin !== targetSpin || date !== targetDate) {
      if (spin !== targetSpin) setTargetSpin(spin)
      if (date !== targetDate) setTargetDate(date)
      setAmountsTotal(undefined)
      setDemand(undefined)
      return
    }
    if (
      !isLoading &&
      !isNil(electricityDataMonthly) &&
      !isLoadingTargets &&
      !isNil(targetsData) &&
      !isLoadingDemand &&
      !isNil(demandData)
    ) {
      const data = isNil(process.env['NOT_USE_SPIN'])
        ? electricityDataMonthly.data
        : find((item: ElectricityMonthly) => item.spin === spin)(
            electricityDataMonthly.data
          )
      if (!isNil(data)) {
        const compareData = isNil(process.env['NOT_USE_SPIN'])
          ? electricityDataMonthly.compareData
          : find(
              (item: ElectricityMonthly) =>
                item.spin === spin || isEmpty(item.spin)
            )(electricityDataMonthly.compareData)
        const amounts = map(
          (item: { date: string; daily: number | null }) => item.daily
        )(data.amounts)
        const compareDataAmounts = !isNil(compareData)
          ? map((item: { date: string; daily: number | null }) => item.daily)(
              compareData.amounts
            )
          : []
        setAmountsTotal({
          amounts: reduce(
            (sum: number, amount: number | null) => sum + (amount ?? 0),
            0
          )(amounts),
          compareAmounts: reduce(
            (sum: number, amount: number | null) => sum + (amount ?? 0),
            0
          )(compareDataAmounts)
        })
        const { demandData: latest } = electricityDataMonthly
        if (!isNil(latest) && !isNil(demandData)) {
          const { amount, date: demandDate } = demandData
          if (!isNil(amount) && !isNil(demandDate)) {
            setDemand({ amount, date: demandDate, latest })
          }
        }
        if (isEmpty(compareDataAmounts)) {
          notify('no compare data')
        }
        const targetsAmounts =
          !isNil(targetsData) && !isNil(targetsArg)
            ? map(
                (item: {
                  targetKey: string
                  targetValue: string
                  data: Record<string, ElectricityMonthly>
                }) => {
                  const { data: dataSet, targetKey } = item
                  if (!(targetsArg[targetKey] as boolean)) {
                    return undefined
                  }
                  const data = dataSet[join(',')(showKinds)]
                  if (isNil(data)) return undefined
                  return {
                    key:
                      targetKey === 'addressState'
                        ? '都道府県'
                        : targetKey === 'addressCity'
                        ? '市区町村'
                        : targetKey,
                    data: map(
                      (item: { date: string; daily: number | null }) =>
                        item.daily
                    )(data.amounts)
                  }
                }
              )(targetsData)
            : undefined
        const { maxTemp, minTemp } = data
        const {
          date: compareDate,
          maxTemp: compareMaxTemp,
          minTemp: compareMinTemp
        } = compareData ?? {
          date: undefined,
          maxTemp: undefined,
          minTemp: undefined
        }
        createMonthlyChart(
          date,
          amounts,
          compareDataAmounts,
          targetsAmounts,
          { date, maxTemp, minTemp },
          {
            date: compareDate,
            maxTemp: compareMaxTemp,
            minTemp: compareMinTemp,
            type: 'monthly'
          },
          translate(`resources.spots.monthly.compareTo.${compareTo as string}`),
          configChart,
          classification
        )
          .then((chart: ChartConfiguration) => {
            setChart(chart)
          })
          .catch(log.error)
      }
    }
  }, [
    spin,
    partner,
    electricityDataMonthly,
    isLoading,
    showKinds,
    targetsArg,
    isLoadingTargets,
    targetsData,
    targetSpin,
    targetDate,
    isLoadingDemand,
    demandData
  ])
  return (
    <>
      {!isNil(amountsId) && (
        <AmountLabel amountsId={amountsId} amountsTotal={amountsTotal} />
      )}
      {!isNil(demandId) && (
        <DemandLabel
          partner={partner}
          demandId={demandId}
          demand={demand}
          latestLabel="対象月"
          latestFormat="M月d日 H:mm"
        />
      )}
      {isLoading || isLoadingTargets ? (
        <Loading />
      ) : !isNil(chart) ? (
        <SolarChart chart={chart} />
      ) : null}
    </>
  )
}
export const ElectricityChartMonthlyHorizontal: React.FC<
  {
    spin: string
    partner: string
    compareTo: ElectricityChartMonthlyKind
    date: string
    classification: string | null | undefined
  } & ConfigProps
> = ({ spin, partner, compareTo, date, configChart, classification }) => {
  const translate = useTranslate()
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const { data: electricityDataMonthly, isLoading } = isNil(
    process.env['NOT_USE_SPIN']
  )
    ? useGetElectricityMonthlyBySpin(date, spin, compareTo)
    : useGetElectricityMonthly(date, compareTo)
  React.useEffect(() => {
    if (!isLoading && !isNil(electricityDataMonthly)) {
      const data = isNil(process.env['NOT_USE_SPIN'])
        ? electricityDataMonthly.data
        : find((item: ElectricityMonthly) => item.spin === spin)(
            electricityDataMonthly.data
          )
      if (!isNil(data)) {
        const compareData = isNil(process.env['NOT_USE_SPIN'])
          ? electricityDataMonthly.compareData
          : find(
              (item: ElectricityMonthly) =>
                item.spin === spin || isEmpty(item.spin)
            )(electricityDataMonthly.compareData)
        const amounts = [
          reduce(
            (sum: number, item: { date: string; daily: number | null }) =>
              (sum += isNumber(item.daily) ? item.daily : 0),
            0
          )(data.amounts)
        ]
        const compareDataAmounts = !isNil(compareData)
          ? [
              reduce(
                (sum: number, item: { date: string; daily: number | null }) =>
                  (sum += isNumber(item.daily) ? item.daily : 0),
                0
              )(compareData.amounts)
            ]
          : []
        createMonthlyHorizontalChart(
          amounts,
          compareDataAmounts,
          translate(`resources.spots.monthly.compareTo.${compareTo as string}`),
          configChart,
          classification
        )
          .then((chart: ChartConfiguration) => {
            setChart(chart)
          })
          .catch(log.error)
      }
    }
  }, [spin, partner, electricityDataMonthly, isLoading])
  if (isLoading) return <Loading />
  if (isNil(chart)) return <NotFound />
  return <SolarChart chart={chart} height={defaultHeight / 2} />
}
export const ElectricityChartYearly: React.FC<
  {
    spin: string
    partner: string
    compareTo: ElectricityChartYearlyKind
    targets: Record<string, boolean> | undefined
    date: string
    inputDate: string
    classification: string | null | undefined
    record: RaRecord
    showKinds: string[] | undefined
    amountsId?: string | undefined
    demandId?: string | undefined
  } & ConfigProps
> = ({
  spin,
  partner,
  compareTo,
  targets: targetsArg,
  date,
  inputDate,
  configChart,
  record,
  classification,
  showKinds,
  amountsId,
  demandId
}) => {
  const translate = useTranslate()
  // const { permissions, isLoading: isLoadingPermissions } = usePermissions()
  const notify = useNotify()
  const targets = !isNil(targetsArg) ? keys(targetsArg) : undefined
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const [amountsTotal, setAmountsTotal] = React.useState<
    | {
        amounts: number
        compareAmounts: number
      }
    | undefined
  >(undefined)
  const [demand, setDemand] = React.useState<
    | {
        amount: number
        date: string
        latest: { amount: number | undefined; date: string | undefined }
      }
    | undefined
    | null
  >(undefined)
  const [targetSpin, setTargetSpin] = React.useState<string | undefined>(
    undefined
  )
  const [targetDate, setTargetDate] = React.useState<string | undefined>(
    undefined
  )
  const { data: electricityDataYearly, isLoading } = isNil(
    process.env['NOT_USE_SPIN']
  )
    ? useGetElectricityYearlyBySpin(date, spin, compareTo, !isNil(demandId))
    : useGetElectricityYearly(date, compareTo)
  const { data: targetsData, isLoading: isLoadingTargets } =
    useGetElectricityYearlyTargets(
      date,
      compareTo,
      isNil(targets) ? undefined : targets,
      isNil(targets)
        ? undefined
        : map((target: string) => record[target])(targets)
    )
  const { data: demandData, isLoading: isLoadingDemand } =
    useGetElectricityDemand(inputDate, spin)
  React.useEffect(() => {
    if (spin !== targetSpin || date !== targetDate) {
      if (spin !== targetSpin) setTargetSpin(spin)
      if (date !== targetDate) setTargetDate(date)
      setAmountsTotal(undefined)
      setDemand(undefined)
      return
    }
    if (
      !isLoading &&
      !isNil(electricityDataYearly) &&
      !isLoadingTargets &&
      !isNil(targetsData) &&
      !isLoadingDemand &&
      !isNil(demandData)
    ) {
      const data = isNil(process.env['NOT_USE_SPIN'])
        ? electricityDataYearly.data
        : find((item: ElectricityYearly) => item.spin === spin)(
            electricityDataYearly.data
          )
      if (!isNil(data)) {
        const compareData = isNil(process.env['NOT_USE_SPIN'])
          ? electricityDataYearly.compareData
          : find((item: ElectricityYearly) => item.spin === spin)(
              electricityDataYearly.compareData
            )
        const amounts = map(
          (item: { date: string; daily: number | null }) => item.daily
        )(data.amounts)
        const lastYearAmounts = !isNil(compareData)
          ? map((item: { date: string; daily: number | null }) => item.daily)(
              compareData.amounts
            )
          : []
        if (isEmpty(lastYearAmounts)) {
          notify('no compare data')
        }
        setAmountsTotal({
          amounts: reduce(
            (sum: number, amount: number | null) => sum + (amount ?? 0),
            0
          )(amounts),
          compareAmounts: reduce(
            (sum: number, amount: number | null) => sum + (amount ?? 0),
            0
          )(lastYearAmounts)
        })
        const targetsAmounts =
          !isNil(targetsData) && !isNil(targetsArg)
            ? map(
                (item: {
                  targetKey: string
                  targetValue: string
                  data: Record<string, ElectricityYearly>
                }) => {
                  const { data: dataSet, targetKey } = item
                  if (!(targetsArg[targetKey] as boolean)) {
                    return undefined
                  }
                  const data = dataSet[join(',')(showKinds)]
                  if (isNil(data)) return undefined
                  return {
                    key:
                      targetKey === 'addressState'
                        ? '都道府県'
                        : targetKey === 'addressCity'
                        ? '市区町村'
                        : targetKey,
                    data: map(
                      (item: { date: string; daily: number | null }) =>
                        item.daily
                    )(data.amounts)
                  }
                }
              )(targetsData)
            : undefined
        const { demandData: latest } = electricityDataYearly
        if (!isNil(latest) && !isNil(demandData)) {
          const { amount, date: demandDate } = demandData
          if (!isNil(amount) && !isNil(demandDate)) {
            setDemand({ amount, date: demandDate, latest })
          }
        }
        const { maxTemp, minTemp } = data
        const {
          date: compareDate,
          maxTemp: compareMaxTemp,
          minTemp: compareMinTemp
        } = compareData ?? {
          date: undefined,
          maxTemp: undefined,
          minTemp: undefined
        }

        const chart = createYearlyChart(
          date,
          amounts,
          lastYearAmounts,
          targetsAmounts,
          { date, maxTemp, minTemp },
          {
            date: compareDate,
            maxTemp: compareMaxTemp,
            minTemp: compareMinTemp,
            type: 'yearly'
          },
          translate(`resources.spots.yearly.compareTo.${compareTo as string}`),
          configChart,
          classification
        )
        setChart(chart)
      }
    }
  }, [
    spin,
    partner,
    electricityDataYearly,
    isLoading,
    showKinds,
    targetsArg,
    isLoadingTargets,
    targetsData,
    targetSpin,
    targetDate,
    isLoadingDemand,
    demandData
  ])
  return (
    <>
      {!isNil(amountsId) && (
        <AmountLabel amountsId={amountsId} amountsTotal={amountsTotal} />
      )}
      {!isNil(demandId) && (
        <DemandLabel
          partner={partner}
          demandId={demandId}
          demand={demand}
          latestLabel="今年度"
          latestFormat="yyyy年M月d日 H:mm"
        />
      )}
      {isLoading || isLoadingTargets ? (
        <Loading />
      ) : !isNil(chart) ? (
        <SolarChart chart={chart} />
      ) : null}
    </>
  )
}
